import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  useGetCarbonProgramByFarmId,
  useGetFootprintingDairyFeedProduction,
  useMutateFootprintingDairyFeedProduction,
  useTranslation,
} from "utils";

import { isMainCommodity } from "consts";

import {
  DAIRY_FEED_PRODUCTION_UNITS,
  DairyFeedProduction,
  ENUM_DAIRY_FEED_PRODUCTION_UNITS,
  ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE,
  FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE,
} from "models";

import { AlertBanner, Button, Card, Drawer, Flex, Form, FormInstance, InputNumber, InputNumberWithUnitSelector, KlimRadioGroup, Spin } from "components";

interface FeedProductionFormData {
  cropUsedForFeed: ENUM_FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE | null;
  cropYieldTPerHa: number | null;
  dieselConsumptionL: number | null;
  energyUsedKwh: number | null;
  mineralNitrogenKgHa: number | null;
  mineralPhosphorusKgHa: number | null;
  mineralPotassiumKgHa: number | null;
  nitrogenOrganicFertKgHa: number | null;
  totalLimeAppliedKgCaCo3PerHa: number | null;
  totalPlantProtectionApplied: number | null;
  totalPlantProtectionAppliedUnit: ENUM_DAIRY_FEED_PRODUCTION_UNITS | null;
  totalWaterUsedIrregationLPerHa: number | null;
  soldOffFarmPct: number | null;
  fedToCowsPct: number | null;
  otherUsesPct: number | null;
}

interface FootprintingDairyFeedProductionDrawerProps {
  farmId: string;
  visible: boolean;
  onClose: () => void;
  feedInformationId: string;
  primaryDataId?: string;
  feedTypeTranslationSlug?: string;
  feedTypeName?: string;
  readOnly?: boolean;
}

export const FootprintingDairyFeedProductionDrawer = ({
  farmId,
  visible,
  onClose,
  feedInformationId,
  primaryDataId,
  feedTypeTranslationSlug,
  feedTypeName,
  readOnly = false,
}: FootprintingDairyFeedProductionDrawerProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);
  const [saving, setSaving] = useState(false);
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const [formData, setFormData] = useState<FeedProductionFormData | null>(null);

  const { data: feedProductionQuery, isLoading } = useGetFootprintingDairyFeedProduction(feedInformationId ?? "");
  const { mutate: mutateFeedProduction } = useMutateFootprintingDairyFeedProduction(feedInformationId, () => {
    setSaving(false);
    onClose();
  });

  const updateFormData = <K extends keyof FeedProductionFormData>(field: K, value: FeedProductionFormData[K]) => {
    setFormData((prev) => ({ ...prev!, [field]: value }));
  };

  const mainCommodity = isMainCommodity(feedTypeTranslationSlug);
  const icon = mainCommodity ? "🌾" : "🌱";

  // Check if dairy is enabled and if program crops are null/empty
  const isDairyEnabled = carbonProgram?.platformFeatures.footprinting?.dairy ?? false;
  const hasProgramCrops = carbonProgram?.farmLevelFootprinting?.programCrops && carbonProgram?.farmLevelFootprinting.programCrops.length > 0;
  // Show extra questions if:
  // 1. It's not a main commodity (existing behavior), OR
  // 2. It's a main commodity AND dairy is enabled AND there are no program crops
  const showExtraQuestions = !mainCommodity || (mainCommodity && isDairyEnabled && !hasProgramCrops);

  useEffect(() => {
    if (feedProductionQuery) {
      const newFormData: FeedProductionFormData = {
        cropUsedForFeed: feedProductionQuery.cropUsedForFeed ?? null,
        cropYieldTPerHa: feedProductionQuery.cropYieldTPerHa ?? null,
        dieselConsumptionL: feedProductionQuery.dieselConsumptionL ?? null,
        energyUsedKwh: feedProductionQuery.energyUsedKwh ?? null,
        mineralNitrogenKgHa: feedProductionQuery.mineralNitrogenKgHa ?? null,
        mineralPhosphorusKgHa: feedProductionQuery.mineralPhosphorusKgHa ?? null,
        mineralPotassiumKgHa: feedProductionQuery.mineralPotassiumKgHa ?? null,
        nitrogenOrganicFertKgHa: feedProductionQuery.nitrogenOrganicFertKgHa ?? null,
        totalLimeAppliedKgCaCo3PerHa: feedProductionQuery.totalLimeAppliedKgCaCo3PerHa ?? null,
        totalPlantProtectionApplied: feedProductionQuery.totalPlantProtectionApplied ?? null,
        totalPlantProtectionAppliedUnit: feedProductionQuery.totalPlantProtectionAppliedUnit ?? null,
        totalWaterUsedIrregationLPerHa: feedProductionQuery.totalWaterUsedIrregationLPerHa ?? null,
        soldOffFarmPct: feedProductionQuery.soldOffFarmPct ?? null,
        fedToCowsPct: feedProductionQuery.fedToCowsPct ?? null,
        otherUsesPct: feedProductionQuery.otherUsesPct ?? null,
      };

      setFormData(newFormData);

      setTimeout(() => {
        const formValues = {
          cropUsedForFeed: newFormData.cropUsedForFeed,
          cropYieldTPerHa: newFormData.cropYieldTPerHa,
          dieselConsumptionL: newFormData.dieselConsumptionL,
          energyUsedKwh: newFormData.energyUsedKwh,
          mineralNitrogenKgHa: newFormData.mineralNitrogenKgHa,
          mineralPhosphorusKgHa: newFormData.mineralPhosphorusKgHa,
          mineralPotassiumKgHa: newFormData.mineralPotassiumKgHa,
          nitrogenOrganicFertKgHa: newFormData.nitrogenOrganicFertKgHa,
          totalLimeAppliedKgCaCo3PerHa: newFormData.totalLimeAppliedKgCaCo3PerHa,
          totalPlantProtectionApplied: newFormData.totalPlantProtectionApplied,
          totalPlantProtectionAppliedUnit: newFormData.totalPlantProtectionAppliedUnit,
          totalWaterUsedIrregationLPerHa: newFormData.totalWaterUsedIrregationLPerHa,
          soldOffFarmPct: formatFractionInput(newFormData.soldOffFarmPct),
          fedToCowsPct: formatFractionInput(newFormData.fedToCowsPct),
          otherUsesPct: formatFractionInput(newFormData.otherUsesPct),
        };

        formRef.current?.setFieldsValue(formValues);
      }, 0);
    }
  }, [feedProductionQuery]);

  const handleSave = () => {
    setSaving(true);

    const productionData: DairyFeedProduction = {
      feedInformationId,
      primaryDataId,
      ...formData,
    };

    mutateFeedProduction(productionData);
  };

  if (isLoading || !formData) {
    return (
      <Drawer visible={visible} onClose={onClose} title={t("dairyFeedProductionForm.title")}>
        <Flex justify="center" align="center">
          <Spin />
        </Flex>
      </Drawer>
    );
  }

  return (
    <Drawer visible={visible} onClose={onClose} title={t("dairyFeedProductionForm.title")}>
      <Flex vertical justify="space-between" className="footprinting-feed-drawer">
        <Form ref={formRef}>
          <Flex vertical gap={24}>
            {/* Header */}
            <Flex vertical className="margin-bottom-250">
              <h1 className="margin-bottom-200">{t("dairyFeedProductionForm.subTitle")}</h1>
              <Flex align="center" gap={12}>
                <span className="drawer-icon-container">{icon}</span> <h5>{feedTypeName}</h5>
              </Flex>
            </Flex>

            {/* Alert banner */}
            {mainCommodity && hasProgramCrops && (
              <AlertBanner
                className="feed-type-alert-banner margin-bottom-100"
                description={t("dairyFeedProductionForm.alertDescription", { cropName: feedTypeName })}
                iconName="tabler/info-circle"
                iconSize={24}
                iconAlign="start"
              />
            )}

            {showExtraQuestions && (
              <Flex vertical gap={16}>
                {/* Crop Yield */}
                <Card className="feed-card padding-100">
                  <h5 className="margin-bottom-175">{t("dairyFeedProductionForm.cropYield")}</h5>
                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="crop-yield-input"
                      name="cropYieldTPerHa"
                      label={t("dairyFeedProductionForm.question.cropYieldTPerHa")}
                      suffix="t/ha"
                      value={formData.cropYieldTPerHa}
                      initialValue={formData.cropYieldTPerHa}
                      onChange={(v) => updateFormData("cropYieldTPerHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>

                {/* Energy */}
                <Card className="feed-card padding-100">
                  <h5 className="margin-bottom-175">{t("dairyFeedProductionForm.energyUsed")}</h5>

                  <AlertBanner
                    className="feed-type-alert-banner margin-bottom-100"
                    description={t("dairyFeedProductionForm.energyAlert")}
                    iconName="tabler/info-circle"
                    iconSize={24}
                    iconAlign="start"
                  />

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="diesel-consumption-input"
                      name="dieselConsumptionL"
                      label={t("dairyFeedProductionForm.question.dieselConsumptionL")}
                      suffix="liter / ha"
                      tooltip={t("dairyFeedProductionForm.tooltips.dieselConsumption")}
                      value={formData.dieselConsumptionL}
                      initialValue={formData.dieselConsumptionL}
                      onChange={(v) => updateFormData("dieselConsumptionL", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                  <p className="paragraph-s grey-600-text margin-top-25 margin-left-50">{t("dairyFeedProductionForm.description.dieselConsumption")}</p>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="energy-used-input"
                      name="energyUsedKwh"
                      label={t("dairyFeedProductionForm.question.energyUsedKwh")}
                      suffix="kWh"
                      tooltip={t("dairyFeedProductionForm.tooltips.energyUsed")}
                      value={formData.energyUsedKwh}
                      initialValue={formData.energyUsedKwh}
                      onChange={(v) => updateFormData("energyUsedKwh", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>

                {/* Products Applied */}
                <Card className="feed-card padding-100">
                  <h5 className="margin-bottom-175">{t("dairyFeedProductionForm.productsApplied")}</h5>
                  <Flex vertical gap={16}>
                    <Form.Item>
                      <InputNumber
                        disabled={readOnly}
                        dataTestId="mineral-nitrogen-input"
                        name="mineralNitrogenKgHa"
                        label={t("dairyFeedProductionForm.question.mineralNitrogenKgHa")}
                        suffix="kg / ha"
                        tooltip={t("dairyFeedProductionForm.tooltips.mineralNitrogen")}
                        value={formData.mineralNitrogenKgHa}
                        initialValue={formData.mineralNitrogenKgHa}
                        onChange={(v) => updateFormData("mineralNitrogenKgHa", v)}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputNumber
                        disabled={readOnly}
                        dataTestId="mineral-phosphorus-input"
                        name="mineralPhosphorusKgHa"
                        label={t("dairyFeedProductionForm.question.mineralPhosphorusKgHa")}
                        suffix="kg / ha"
                        tooltip={t("dairyFeedProductionForm.tooltips.mineralPhosphorus")}
                        value={formData.mineralPhosphorusKgHa}
                        initialValue={formData.mineralPhosphorusKgHa}
                        onChange={(v) => updateFormData("mineralPhosphorusKgHa", v)}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputNumber
                        disabled={readOnly}
                        dataTestId="mineral-potassium-input"
                        name="mineralPotassiumKgHa"
                        label={t("dairyFeedProductionForm.question.mineralPotassiumKgHa")}
                        suffix="kg / ha"
                        tooltip={t("dairyFeedProductionForm.tooltips.mineralPotassium")}
                        value={formData.mineralPotassiumKgHa}
                        initialValue={formData.mineralPotassiumKgHa}
                        onChange={(v) => updateFormData("mineralPotassiumKgHa", v)}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputNumber
                        disabled={readOnly}
                        dataTestId="organic-fertilizer-input"
                        name="nitrogenOrganicFertKgHa"
                        label={t("dairyFeedProductionForm.question.nitrogenOrganicFertKgHa")}
                        suffix="kg / ha"
                        tooltip={t("dairyFeedProductionForm.tooltips.nitrogenOrganicFert")}
                        value={formData.nitrogenOrganicFertKgHa}
                        initialValue={formData.nitrogenOrganicFertKgHa}
                        onChange={(v) => updateFormData("nitrogenOrganicFertKgHa", v)}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputNumber
                        disabled={readOnly}
                        dataTestId="lime-applied-input"
                        name="totalLimeAppliedKgCaCo3PerHa"
                        label={t("dairyFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa")}
                        suffix="kg CaCO₃ / ha"
                        tooltip={t("dairyFeedProductionForm.tooltips.totalLimeApplied")}
                        value={formData.totalLimeAppliedKgCaCo3PerHa}
                        initialValue={formData.totalLimeAppliedKgCaCo3PerHa}
                        onChange={(v) => updateFormData("totalLimeAppliedKgCaCo3PerHa", v)}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item>
                      <InputNumberWithUnitSelector
                        disabled={readOnly}
                        dataTestId="plant-protection-input"
                        name="totalPlantProtectionApplied"
                        label={t("dairyFeedProductionForm.question.totalPlantProtectionApplied")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        units={Object.values(DAIRY_FEED_PRODUCTION_UNITS)}
                        tooltip={t("dairyFeedProductionForm.tooltips.totalPlantProtectionApplied")}
                        value={formData.totalPlantProtectionApplied}
                        initialValue={formData.totalPlantProtectionApplied}
                        unitValue={formData.totalPlantProtectionAppliedUnit}
                        onChange={(v, unit) => {
                          setFormData((prev) => ({
                            ...prev!,
                            totalPlantProtectionApplied: v ?? prev!.totalPlantProtectionApplied,
                            totalPlantProtectionAppliedUnit: unit as ENUM_DAIRY_FEED_PRODUCTION_UNITS,
                          }));
                        }}
                        validateFirst
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>
                  </Flex>
                </Card>

                {/* Irrigation */}
                <Card className="feed-card padding-100">
                  <h5 className="margin-bottom-175">{t("dairyFeedProductionForm.irrigation")}</h5>
                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="water-irrigation-input"
                      name="totalWaterUsedIrregationLPerHa"
                      label={t("dairyFeedProductionForm.question.totalWaterUsedIrregationLPerHa")}
                      suffix="liter / ha"
                      tooltip={t("dairyFeedProductionForm.tooltips.totalWaterUsedIrregation")}
                      value={formData.totalWaterUsedIrregationLPerHa}
                      initialValue={formData.totalWaterUsedIrregationLPerHa}
                      onChange={(v) => updateFormData("totalWaterUsedIrregationLPerHa", v)}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validNumber")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Card>
              </Flex>
            )}

            {/* Crop Usage Radio */}
            <Card className="padding-100 feed-card">
              <h5 className="margin-bottom-100">{t("dairyFeedProductionForm.question.cropUsageRadioTitle")}</h5>
              <Flex align="center" gap={16} justify="space-between">
                <span>
                  {t("dairyFeedProductionForm.question.cropUsageRadioDescription")}
                  <span className="required-asterisk">*</span>
                </span>
                <KlimRadioGroup
                  required
                  name="cropUsedForFeed"
                  dataTestId="crop-used-for-feed-radio"
                  disabled={readOnly}
                  vertical={false}
                  type="tertiary"
                  size="large"
                  onChange={({ target }) => updateFormData("cropUsedForFeed", target.value)}
                  value={formData.cropUsedForFeed}
                  optionType="button"
                  options={[FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.YES, FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO].map((usage) => ({
                    label: t(`dairyFeedProductionForm.cropUsageOptions.${usage}`),
                    value: usage,
                  }))}
                />
              </Flex>

              {/* Production Fields - Show if NO */}
              {formData.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO && (
                <Flex vertical gap={16} className="margin-top-150">
                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="sold-off-farm-input"
                      name="soldOffFarmPct"
                      label={t("dairyFeedProductionForm.question.soldOffFarmPct")}
                      suffix={t("dairyFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("dairyFeedProductionForm.tooltips.soldOffFarm")}
                      value={formatFractionInput(formData.soldOffFarmPct)}
                      initialValue={formatFractionInput(formData.soldOffFarmPct)}
                      onChange={(v) => updateFormData("soldOffFarmPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="fed-to-cows-input"
                      name="fedToCowsPct"
                      label={t("dairyFeedProductionForm.question.fedToCowsPct")}
                      suffix={t("dairyFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("dairyFeedProductionForm.tooltips.fedToCows")}
                      value={formatFractionInput(formData.fedToCowsPct)}
                      initialValue={formatFractionInput(formData.fedToCowsPct)}
                      onChange={(v) => updateFormData("fedToCowsPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      dataTestId="other-uses-input"
                      name="otherUsesPct"
                      label={t("dairyFeedProductionForm.question.otherUsesPct")}
                      suffix={t("dairyFeedProductionForm.question.totalPctSuffix")}
                      tooltip={t("dairyFeedProductionForm.tooltips.otherUses")}
                      value={formatFractionInput(formData.otherUsesPct)}
                      initialValue={formatFractionInput(formData.otherUsesPct)}
                      onChange={(v) => updateFormData("otherUsesPct", formatPercentageInput(v))}
                      precision={2}
                      rules={[
                        {
                          required: true,
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("dairyFeedProductionForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Flex>
              )}
            </Card>
          </Flex>
        </Form>
        <Button className="margin-top-200" type="primary" onClick={handleSave} loading={saving} disabled={readOnly} dataTestId="save-button">
          {t("dairyFeedProductionForm.saveAndClose")}
        </Button>
      </Flex>
    </Drawer>
  );
};
